import { useState, useEffect, useRef } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import * as api from '../lib/api'
import { DATE_FORMAT_LABEL, formatDate } from '../lib/dateFormat'
import { Card, Table, Button, Tag, Row, Col, Input, InputNumber, Space, Typography, Select, DatePicker, Tabs, Checkbox, Switch, message } from 'antd'
import Popconfirm from '../components/Popconfirm'
import { PlusOutlined } from '@ant-design/icons'
import BusinessUnitManager from './BusinessUnitManager'
import ManagerDigestExclusions from './ManagerDigestExclusions'
import OrganizationManager from './OrganizationManager'
import { useI18n } from '../i18n/I18nProvider'
import Title from 'antd/es/skeleton/Title'

const { Text } = Typography
const HR_TAB_KEYS = ['sessions']
const statusColors = { pending_employee: 'orange', pending_employee_review: 'gold', pending_manager: 'blue', pending_head: 'purple', pending_bu_head: 'geekblue', pending_approver_4: 'cyan', pending_approver_5: 'magenta', completed: 'green' }
const statusLabelFallbacks = { pending_employee: 'Pending Employee', pending_employee_review: 'Pending Employee Review', pending_manager: 'Awaiting Approver 1 approval', pending_head: 'Awaiting Approver 2 approval', pending_bu_head: 'Awaiting Approver 3 approval', pending_approver_4: 'Awaiting Approver 4 approval', pending_approver_5: 'Awaiting Approver 5 approval', completed: 'Completed' }
const sessionStatusFallbacks = { draft: 'Draft', active: 'Active', closed: 'Closed' }
const PROCESS_OVERVIEW_EMAILS_ENABLED = process.env.REACT_APP_PROCESS_OVERVIEW_EMAILS_ENABLED === 'true'

export function finalSessionDeadlineDate(source = {}) {
  return source.deadline_date ||
    source.session_deadline_date ||
    source.approver_5_stage_end_date ||
    source.approver_4_stage_end_date ||
    source.bu_head_stage_end_date ||
    source.department_head_stage_end_date ||
    source.manager_stage_end_date ||
    source.employee_stage_end_date ||
    null
}

export function isHardDeadlinePassed(source = {}, now = new Date()) {
  if (source.session_deadline_type !== 'hard' || !source.session_deadline_date) return false
  const today = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0')
  ].join('-')
  return String(source.session_deadline_date).slice(0, 10) < today
}

export function delegationOptionsFromFlow(flow) {
  if (flow === 'both') return ['direct_transfer', 'functional_review']
  if (flow === 'functional_review') return ['functional_review']
  if (flow === 'direct_transfer') return ['direct_transfer']
  return []
}

export function flowFromDelegationOptions(options = []) {
  const enabled = new Set(options)
  const direct = enabled.has('direct_transfer')
  const indirect = enabled.has('functional_review')
  if (direct && indirect) return 'both'
  if (indirect) return 'functional_review'
  if (direct) return 'direct_transfer'
  return 'disabled'
}

const emptySessionForm = {
  name: '',
  display_title: '2026 Performance Appraisal',
  template: '',
  description: '',
  manager_review_flow: 'functional_review',
  deadline_type: 'soft',
  status: 'draft',
  employee_stage_start_date: null,
  employee_stage_end_date: null,
  manager_stage_start_date: null,
  manager_stage_end_date: null,
  department_head_stage_start_date: null,
  department_head_stage_end_date: null,
  bu_head_stage_start_date: null,
  bu_head_stage_end_date: null,
  performance_period_start_date: null,
  performance_period_end_date: null,
  rating_justification_min_length: 10
}

const reviewPeriodStageFields = [
  { labelKey: 'session.review_periods.employee', labelFallback: 'Employee Review Period', startField: 'employee_stage_start_date', endField: 'employee_stage_end_date' },
  { labelKey: 'session.review_periods.approver_1', labelFallback: 'Approver 1 Review Period', startField: 'manager_stage_start_date', endField: 'manager_stage_end_date' },
  { labelKey: 'session.review_periods.approver_2', labelFallback: 'Approver 2 Review Period', startField: 'department_head_stage_start_date', endField: 'department_head_stage_end_date' },
  { labelKey: 'session.review_periods.approver_3', labelFallback: 'Approver 3 Review Period', startField: 'bu_head_stage_start_date', endField: 'bu_head_stage_end_date' }
]

const buildSessionPayload = form => {
  const payload = { ...form }
  if (payload.template !== 'vietnam_milwaukee') delete payload.rating_justification_min_length
  return payload
}

export function StatusBadge({ status, label }) {
  const { t } = useI18n()
  const text = statusLabelFallbacks[status] ? t(`appraisal.status.${status}`, statusLabelFallbacks[status]) : label || status
  return (
    <Tag
      color={statusColors[status] || 'default'}
      style={{
        display: 'inline-block',
        boxSizing: 'border-box',
        maxWidth: '100%',
        height: 'auto',
        marginInlineEnd: 0,
        whiteSpace: 'normal',
        overflowWrap: 'break-word'
      }}
    >
      {text}
    </Tag>
  )
}

export function approverName(source, level, fallback = 'the approver') {
  if (!source) return fallback
  const prefix = `approver_${level}`
  const legacyPrefix = { 1: 'manager', 2: 'department_head', 3: 'bu_head' }[level]
  const fullName = personLabel(
    source[`${prefix}_first_name`],
    source[`${prefix}_last_name`],
    '',
    '',
    source.session_template
  )
  const legacyFullName = personLabel(
    source[`${legacyPrefix}_first_name`],
    source[`${legacyPrefix}_last_name`],
    '',
    '',
    source.session_template
  )
  if (isVietnamNameOrder(source.session_template)) {
    return fullName !== '—'
      ? fullName
      : legacyFullName !== '—'
        ? legacyFullName
        : source[`${prefix}_email`] || source[`${legacyPrefix}_email`] || fallback
  }
  return source[`${prefix}_name`] || (fullName !== '—' ? fullName : '') || source[`${prefix}_email`] || source[`${legacyPrefix}_name`] || (legacyFullName !== '—' ? legacyFullName : '') || source[`${legacyPrefix}_email`] || fallback
}

export function isVietnamNameOrder(templateCode) {
  return ['vietnam_tti', 'vietnam_milwaukee'].includes(templateCode)
}

export function personLabel(firstName, lastName, email, position, templateCode) {
  const nameParts = isVietnamNameOrder(templateCode)
    ? [lastName, firstName]
    : [firstName, lastName]
  const name = nameParts.filter(Boolean).join(' ').trim()
  return name || email || '—'
}


function daysUntil(dateStr) {
  if (!dateStr) return null
  const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24))
  return diff
}

function DeadlineBadge({ days, t }) {
  if (days === null) return <Text type='secondary'>—</Text>
  if (days < 0) return <Tag color='red'>{t('dashboard.deadline.overdue_by_days', 'Overdue by {{days}}d', { days: Math.abs(days) })}</Tag>
  if (days <= 7) return <Tag color='orange'>{t('dashboard.deadline.days_left', '{{days}}d left', { days })}</Tag>
  return <Tag color='green'>{t('dashboard.deadline.days_left', '{{days}}d left', { days })}</Tag>
}

export default function HRDashboard({ me, canManage = false }) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState({ ...emptySessionForm })
  const [sending, setSending] = useState({})
  const [sendingProcess, setSendingProcess] = useState({})
  const [updatingStatus, setUpdatingStatus] = useState({})
  const [templates, setTemplates] = useState([])
  const [templatesLoading, setTemplatesLoading] = useState(false)
  const [showDeactivated, setShowDeactivated] = useState(false)
  const [tabRefreshKeys, setTabRefreshKeys] = useState({})
  const loadedDataRef = useRef(new Set())
  const canManageTemplates = Boolean(me?.is_staff || me?.is_superuser)
  // The Translations tab is deprecated/unused and intentionally hidden for all HR roles.
  const allowedTabKeys = [
    ...HR_TAB_KEYS,
    ...(canManage ? ['organization'] : []),
    ...(canManage ? ['email-notifications'] : []),
    ...(canManageTemplates ? ['templates'] : [])
  ]
  const activeTab = allowedTabKeys.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'sessions'
  const visibleSessions = showDeactivated ? sessions : sessions.filter(session => session.status === 'active')
  const createReviewPeriodStageFields = reviewPeriodStageFields.map(item => ({
    ...item,
    label: t(item.labelKey, item.labelFallback)
  }))
  const delegationCheckboxOptions = [
    { value: 'direct_transfer', label: t('delegation.type.direct', 'Direct delegation') },
    { value: 'functional_review', label: t('delegation.type.indirect', 'Indirect delegation') }
  ]

  useEffect(() => {
    if (!['sessions'].includes(activeTab)) return
    setLoading(true)
    api.getSessions()
      .then(setSessions)
      .catch(e => message.error(e.message || t('dashboard.messages.sessions_load_failed', 'Unable to load sessions')))
      .finally(() => setLoading(false))
  }, [activeTab, t])


  async function openCreateSession() {
    setShowCreate(true)
    if (loadedDataRef.current.has('templates')) return
    loadedDataRef.current.add('templates')
    setTemplatesLoading(true)
    try {
      const templateOptions = await api.getTemplateOptions()
      setTemplates(templateOptions)
      const firstTemplate = templateOptions[0]
      if (firstTemplate) {
        setForm(previous => previous.template
          ? previous
          : { ...previous, template: firstTemplate.code })
      }
    } catch (e) {
      loadedDataRef.current.delete('templates')
      message.error(e.message || t('templates.messages.load_failed', 'Unable to load templates'))
    } finally {
      setTemplatesLoading(false)
    }
  }

  async function createSession() {
    const missing = []
    if (!form.name) missing.push(t('dashboard.create_session.session_name', 'Session Name'))
    if (!form.display_title) missing.push(t('session.fields.display_title', 'Display Title'))
    if (!form.template) missing.push(t('dashboard.create_session.template', 'Template'))
    if (!form.performance_period_start_date) missing.push(t('session.performance_period.start', 'Performance period start'))
    if (!form.performance_period_end_date) missing.push(t('session.performance_period.end', 'Performance period end'))
    if (form.performance_period_start_date?.isAfter(form.performance_period_end_date)) {
      missing.push(t('session.validation.performance_period_order', 'Performance period start must be on or before its end'))
    }
    const periodErrors = []
    createReviewPeriodStageFields.forEach(({ label, startField, endField }) => {
      if (!form[startField] || !form[endField]) {
        periodErrors.push(t('session.validation.stage_dates_required', '{{stage}}: both dates required', { stage: label }))
        return
      }
      if (form[startField].isAfter(form[endField])) {
        periodErrors.push(t('session.validation.stage_date_order', '{{stage}}: start must be on or before end', { stage: label }))
      }
    })
    if (missing.length || periodErrors.length) {
      window.alert([t('common.please_fix', 'Please fix:'), ...missing.map(m => `- ${m}`), ...periodErrors.map(m => `- ${m}`)].join('\n'))
      return
    }
    try {
      const payload = buildSessionPayload(form)
      createReviewPeriodStageFields.forEach(({ startField, endField }) => {
        payload[startField] = form[startField].format('YYYY-MM-DD')
        payload[endField] = form[endField].format('YYYY-MM-DD')
      })
      payload.performance_period_start_date = form.performance_period_start_date.format('YYYY-MM-DD')
      payload.performance_period_end_date = form.performance_period_end_date.format('YYYY-MM-DD')
      const s = await api.createSession(payload)
      setSessions([s, ...sessions])
      setForm({ ...emptySessionForm })
      setShowCreate(false)
      navigate(`/sessions/${s.id}`)
    } catch (e) { message.error(e.message || t('dashboard.messages.session_create_failed', 'Unable to create session')) }
  }
  async function triggerEmails(id) {
    setSending(s => ({ ...s, [id]: true }))
    try { const res = await api.triggerSessionEmails(id); window.alert(t('dashboard.alerts.emails_sent', '{{count}} email(s) sent', { count: res.sent })); setSessions(await api.getSessions()) } catch (e) { message.error(e.message || t('dashboard.messages.invitation_emails_failed', 'Unable to send appraisal invitation emails')) }
    setSending(s => ({ ...s, [id]: false }))
  }

  async function sendProcessEmails(id) {
    setSendingProcess(s => ({ ...s, [id]: true }))
    try {
      const res = await api.sendSessionProcessOverviewEmails(id)
      window.alert(t('dashboard.alerts.process_overview_sent_with_senior_approvers', 'Introductory emails sent to {{employees}} employee(s), {{managers}} Approver 1 participant(s), and {{seniorApprovers}} Approver 2/3 participant(s)', {
        employees: res.sent_employees,
        managers: res.sent_managers,
        seniorApprovers: res.sent_senior_approvers
      }))
    } catch (e) {
      message.error(e.message || t('dashboard.messages.process_emails_failed', 'Unable to send process overview emails'))
    }
    setSendingProcess(s => ({ ...s, [id]: false }))
  }
  async function setSessionStatus(id, status) {
    setUpdatingStatus(prev => ({ ...prev, [id]: true }))
    try {
      const updated = await api.patchSession(id, { status })
      setSessions(prev => prev.map(session => (session.id === id ? updated : session)))
    } catch (e) {
      message.error(e.message || t('dashboard.messages.status_update_failed', 'Unable to update session status'))
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [id]: false }))
    }
  }


  const renderReviewPeriodTag = (label, startDate, endDate) => {
    if (!startDate || !endDate) return null
    return (
      <Tag
        key={label}
        color='blue'
        style={{
          display: 'inline-flex',
          width: 230,
          justifyContent: 'space-between',
          marginBottom: 4,
          whiteSpace: 'nowrap'
        }}
      >
        <span style={{ fontWeight: 600 }}>{label}</span>
        <span>{formatDate(startDate)} - {formatDate(endDate)}</span>
      </Tag>
    )
  }

  const sessionCols = [
    { title: t('dashboard.table.name', 'Name'), dataIndex: 'name', key: 'name', width: 220 },
    { title: t('dashboard.table.template', 'Template'), key: 'template', width: 180, render: (_, r) => r.template_name || r.template || '—' },
    {
      title: t('dashboard.table.review_periods', 'Review Periods ({{format}})', { format: DATE_FORMAT_LABEL }),
      key: 'stages',
      width: 290,
      render: (_, r) => (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          {renderReviewPeriodTag(t('session.stages.employee', 'Employee'), r.employee_stage_start_date, r.employee_stage_end_date)}
          {renderReviewPeriodTag(t('session.stages.approver_1', 'Approver 1'), r.manager_stage_start_date, r.manager_stage_end_date)}
          {renderReviewPeriodTag(t('session.stages.approver_2', 'Approver 2'), r.department_head_stage_start_date, r.department_head_stage_end_date)}
          {renderReviewPeriodTag(t('session.stages.approver_3', 'Approver 3'), r.bu_head_stage_start_date, r.bu_head_stage_end_date)}
        </div>
      )
    },
    { title: t('dashboard.table.deadline', 'Deadline'), key: 'dl', width: 110, render: (_, r) => <DeadlineBadge days={daysUntil(finalSessionDeadlineDate(r))} t={t} /> },
    { title: t('dashboard.table.status', 'Status'), dataIndex: 'status', key: 'st', width: 75, render: s => <Tag style={{ margin: 0 }}>{t(`dashboard.status.${s}`, sessionStatusFallbacks[s] || s)}</Tag> },
    {
      title: t('dashboard.table.actions', 'Actions'),
      key: 'actions',
      width: 240,
      render: (_, r) => (
        <Space size={[12, 10]} wrap>
          <Link to={`/sessions/${r.id}`}><Button size='small'>{canManage ? t('dashboard.actions.view_edit', 'View / Edit') : t('dashboard.actions.view', 'View')}</Button></Link>
          {PROCESS_OVERVIEW_EMAILS_ENABLED && canManage && (
            <Popconfirm
              title={t('dashboard.confirm.process_overview_emails.title', 'Send process overview emails?')}
              description={t('dashboard.confirm.process_overview_emails_with_senior_approvers.description', 'This sends the relevant process overview email to employees and Approvers 1, 2, and 3.')}
              okText={t('dashboard.actions.send_process_overview_emails', 'Send Process Overview Emails')}
              cancelText={t('dashboard.actions.cancel', 'Cancel')}
              onConfirm={() => sendProcessEmails(r.id)}
            >
              <Button size='small' loading={sendingProcess[r.id]}>{t('dashboard.actions.send_process_overview_emails', 'Send Process Overview Emails')}</Button>
            </Popconfirm>
          )}
          {canManage && (
            <Popconfirm
              title={t('dashboard.confirm.send_appraisal_invitations.title', 'Send appraisal invitation emails?')}
              description={t('dashboard.confirm.send_appraisal_invitations.description', 'This sends an appraisal invitation to each pending employee and activates the session if it is still in draft.')}
              okText={t('dashboard.actions.send_appraisal_invitations', 'Send Appraisal Invitation Emails')}
              cancelText={t('dashboard.actions.cancel', 'Cancel')}
              onConfirm={() => triggerEmails(r.id)}
            >
              <Button size='small' loading={sending[r.id]}>{t('dashboard.actions.send_appraisal_invitations', 'Send Appraisal Invitation Emails')}</Button>
            </Popconfirm>
          )}
          {canManage && (r.status === 'active'
            ? (
              <Popconfirm
                title={t('dashboard.confirm.deactivate.title', 'Deactivate this session?')}
                description={t('dashboard.confirm.deactivate.description', 'Employees and approvers will no longer be able to continue actions in this session until it is reactivated.')}
                okText={t('dashboard.actions.deactivate', 'Deactivate')}
                cancelText={t('dashboard.actions.cancel', 'Cancel')}
                okButtonProps={{ danger: true }}
                onConfirm={() => setSessionStatus(r.id, 'closed')}
              >
                <Button
                  size='small'
                  loading={updatingStatus[r.id]}
                >
                  {t('dashboard.actions.deactivate', 'Deactivate')}
                </Button>
              </Popconfirm>
            )
            : (
              <Button
                size='small'
                loading={updatingStatus[r.id]}
                onClick={() => setSessionStatus(r.id, 'active')}
              >
                {t('dashboard.actions.activate', 'Activate')}
              </Button>
            ))}
        </Space>
      )
    }
  ]
  function changeTab(key) {
    const next = new URLSearchParams(searchParams)
    next.set('tab', key)
    setSearchParams(next, { replace: true })
    setTabRefreshKeys(previous => ({ ...previous, [key]: (previous[key] || 0) + 1 }))
  }

  return (
    <div>
      <Tabs
        activeKey={activeTab}
        onChange={changeTab}
        items={[
          {
            key: 'sessions',
            label: t('dashboard.tabs.sessions', 'Sessions'),
            children: (
              <Space direction='vertical' size='middle' style={{ width: '100%' }}>
                {showCreate && (
                  <Card size='small' title={t('dashboard.create_session.title', 'Create Appraisal Session')}>
                    <Row gutter={12}>
                      <Col span={12}><label style={{ fontSize: 12, color: 'var(--tti-muted)' }}>{t('dashboard.create_session.session_name', 'Session Name')}</label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Col>
                      <Col span={12}>
                        <label style={{ fontSize: 12, color: 'var(--tti-muted)' }}>{t('session.fields.display_title', 'Display Title')}</label>
                        <Input value={form.display_title} onChange={e => setForm({ ...form, display_title: e.target.value })} style={{ marginTop: 4 }} />
                        <Text type='secondary' style={{ fontSize: 12 }}>{t('session.fields.display_title_help', 'Used as the title on appraisal forms and PDF exports.')}</Text>
                      </Col>
                      <Col span={12} style={{ marginTop: 8 }}>
                        <label style={{ fontSize: 12, color: 'var(--tti-muted)' }}>{t('dashboard.create_session.template', 'Template')}</label>
                        <Select
                          value={form.template || undefined}
                          onChange={value => setForm({ ...form, template: value })}
                          options={templates.map(template => ({ value: template.code, label: template.name }))}
                          placeholder={templates.length ? t('dashboard.create_session.select_template', 'Select a template') : t('common.loading', 'Loading…')}
                          loading={templatesLoading}
                          style={{ width: '100%', marginTop: 4 }}
                          size='small'
                        />
                      </Col>
                      <Col span={24} style={{ marginTop: 8 }}>
                        <Card
                          size='small'
                        >
                          <Title level={4}>{t('session.performance_period.title', 'Employee Performance Period')}</Title>
                          <Row gutter={12}>
                            <Col span={12}>
                              <label style={{ fontSize: 12, color: 'var(--tti-muted)' }}>{t('common.start_with_format', 'Start ({{format}})', { format: DATE_FORMAT_LABEL })} <Text type='danger'>*</Text></label>
                              <DatePicker
                                format='YYYY-MM-DD'
                                value={form.performance_period_start_date}
                                onChange={date => setForm({ ...form, performance_period_start_date: date })}
                                style={{ width: '100%', marginTop: 4 }}
                              />
                            </Col>
                            <Col span={12}>
                              <label style={{ fontSize: 12, color: 'var(--tti-muted)' }}>{t('common.end_with_format', 'End ({{format}})', { format: DATE_FORMAT_LABEL })} <Text type='danger'>*</Text></label>
                              <DatePicker
                                format='YYYY-MM-DD'
                                value={form.performance_period_end_date}
                                onChange={date => setForm({ ...form, performance_period_end_date: date })}
                                style={{ width: '100%', marginTop: 4 }}
                              />
                            </Col>
                          </Row>
                        </Card>
                      </Col>
                      <Col span={24} style={{ marginTop: 8 }}>
                        <Card
                          size='small'
                          extra={<Text type='secondary'>{t('session.review_periods.create_help', 'Set the start and end date for every review period. Session start/end dates are derived from the earliest/latest dates entered here.')}</Text>}
                        >
                          <Title level={4}>{t('session.review_periods.title', 'Review Periods')}</Title>
                          <Table
                            size='small'
                            pagination={false}
                            rowKey={record => record.startField}
                            dataSource={createReviewPeriodStageFields.map(item => ({ ...item, key: item.startField }))}
                            columns={[
                              { title: t('session.review_periods.stage', 'Stage'), dataIndex: 'label', key: 'label', width: 190 },
                              {
                                title: <span>{t('common.start_with_format', 'Start ({{format}})', { format: DATE_FORMAT_LABEL })} <Text type='danger'>*</Text></span>,
                                dataIndex: 'startField',
                                key: 'start',
                                width: 180,
                                render: (_, record) => (
                                  <DatePicker
                                    format='YYYY-MM-DD'
                                    size='small'
                                    value={form[record.startField]}
                                    onChange={d => setForm({ ...form, [record.startField]: d })}
                                    style={{ width: '100%' }}
                                  />
                                )
                              },
                              {
                                title: <span>{t('common.end_with_format', 'End ({{format}})', { format: DATE_FORMAT_LABEL })} <Text type='danger'>*</Text></span>,
                                dataIndex: 'endField',
                                key: 'end',
                                width: 180,
                                render: (_, record) => (
                                  <DatePicker
                                    format='YYYY-MM-DD'
                                    size='small'
                                    value={form[record.endField]}
                                    onChange={d => setForm({ ...form, [record.endField]: d })}
                                    style={{ width: '100%' }}
                                  />
                                )
                              }
                            ]}
                          />
                        </Card>
                      </Col>
                      <Col span={12} style={{ marginTop: 8 }}>
                        <label style={{ fontSize: 12, color: 'var(--tti-muted)' }}>{t('dashboard.create_session.status', 'Status')}</label>
                        <Select
                          value={form.status}
                          onChange={(value) => setForm({ ...form, status: value })}
                          options={[
                            { value: 'draft', label: t('dashboard.status.draft', 'Draft') },
                            { value: 'active', label: t('dashboard.status.active', 'Active') },
                            { value: 'closed', label: t('dashboard.status.closed', 'Closed') }
                          ]}
                          style={{ width: '100%', marginTop: 4 }}
                          size='small'
                        />
                      </Col>
                      <Col span={12} style={{ marginTop: 8 }}>
                        <label style={{ fontSize: 12, color: 'var(--tti-muted)' }}>{t('delegation.manager_delegation', 'Manager Delegation')}</label>
                        <Checkbox.Group
                          value={delegationOptionsFromFlow(form.manager_review_flow)}
                          onChange={(values) => setForm({ ...form, manager_review_flow: flowFromDelegationOptions(values) })}
                          options={delegationCheckboxOptions}
                          style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}
                        />
                        <Text type='secondary' style={{ fontSize: 12 }}>{t('delegation.disabled_help', 'Leave both unchecked to disable delegation for this session.')}</Text>
                      </Col>
                      {form.template === 'vietnam_milwaukee' && (
                        <Col span={12} style={{ marginTop: 8 }}>
                          <label style={{ display: 'block', fontSize: 12, color: 'var(--tti-muted)', marginBottom: 4 }}>Minimum Rating Justification Length</label>
                          <InputNumber
                            min={1}
                            max={1000}
                            value={form.rating_justification_min_length}
                            onChange={value => setForm({ ...form, rating_justification_min_length: value || 10 })}
                            style={{ width: '100%' }}
                          />
                        </Col>
                      )}
                      <Col span={12} style={{ marginTop: 8 }}>
                        <label style={{ display: 'block', fontSize: 12, color: 'var(--tti-muted)', marginBottom: 4 }}>{t('session.deadlines.soft', 'Soft Deadlines')}</label>
                        <Switch
                          checked={form.deadline_type === 'soft'}
                          onChange={checked => setForm({ ...form, deadline_type: checked ? 'soft' : 'hard' })}
                        />
                        <Text type='secondary' style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                          {t('session.deadlines.soft_help', 'Allow submissions after review-period deadlines.')}
                        </Text>
                      </Col>
                      <Col span={24} style={{ marginTop: 8 }}><label style={{ fontSize: 12, color: 'var(--tti-muted)' }}>{t('dashboard.create_session.description', 'Description')}</label><Input.TextArea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></Col>
                    </Row>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                      <Button onClick={() => setShowCreate(false)}>{t('dashboard.actions.cancel', 'Cancel')}</Button>
                      <Button className='tti-btn-primary-grey' onClick={createSession}>{t('dashboard.actions.create', 'Create')}</Button>
                    </div>
                  </Card>
                )}

                <Card
                  size='small'
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <Title level={4} style={{ color: '#FFF', margin: 0 }}>{t('dashboard.sessions.title', 'Sessions')}</Title>
                    <Space size='small'>
                      {canManage && (
                        <Button size='small' icon={<PlusOutlined />} onClick={openCreateSession} className='tti-btn-primary-grey'>{t('dashboard.actions.new_session', 'New Session')}</Button>
                      )}
                      <Button size='small' onClick={() => setShowDeactivated(value => !value)}>
                        {showDeactivated ? t('dashboard.actions.hide_deactivated', 'Hide Deactivated') : t('dashboard.actions.show_deactivated', 'Show Deactivated')}
                      </Button>
                    </Space>
                  </div>

                  <Table dataSource={visibleSessions} columns={sessionCols} rowKey='id' size='small' pagination={false} loading={loading} />
                </Card>
              </Space>
            )
          },
          {
            key: 'organization',
            label: t('dashboard.tabs.organization', 'Organization'),
            children: <OrganizationManager key={`organization-${tabRefreshKeys.organization || 0}`} />
          },
          {
            key: 'email-notifications',
            label: t('digest_exclusions.disable_title', 'Disable digest emails'),
            children: <ManagerDigestExclusions />
          },
          {
            key: 'templates',
            label: t('templates.title', 'Templates'),
            children: <BusinessUnitManager key={`templates-${tabRefreshKeys.templates || 0}`} canManage />
          }
        ].filter(item => allowedTabKeys.includes(item.key))}
      />
    </div>
  )
}
